import React, { useState, useEffect, useRef } from 'react';
import Cookies from 'universal-cookie';
import pathfinder from '@fiverr-private/pathfinder';
import { Container, Grid, GridItem, Stack } from '@fiverr-private/layout_components';
import { Text, Link, Typography } from '@fiverr-private/typography';
import { IconButton } from '@fiverr-private/button';
import { TimesIcon } from '@fiverr-private/visuals';
import { Image } from '@fiverr-private/media';
import { BLACK_FRIDAY_COOKIE_NAME, EXPIRATION_60_DAYS_IN_SECONDS } from '../../constants';
import { translate } from '../../utils/translate';
import { useCountdown } from './useCountdown';
import { gridContainer, gradientText, timerSkeleton } from './BlackFridayBanner.css';
import { DISCOUNT_IMAGE } from './BlackFridayBanner.constants';
import { reportViewedBlackFridayBanner, reportClickedCloseIcon, reportClickedTermsConditionsLink } from './events';
import { getTargetDate } from './utils';

const formatTime = (time: number) => time.toString().padStart(2, '0');

export const BlackFridayBanner: React.FC = () => {
    const [dismissed, setDismissed] = useState(false);
    const [showTimer, setShowTimer] = useState(true);
    const countdown = useCountdown();
    const hasReportedImpression = useRef(false);

    const bannerText = translate('message', { params: { price: 100, percentage: 30 } }).toString();

    useEffect(() => {
        if (!hasReportedImpression.current) {
            reportViewedBlackFridayBanner(bannerText);
            hasReportedImpression.current = true;
        }
    }, [bannerText]);

    useEffect(() => {
        const checkTime = (intervalId?: number) => {
            const now = new Date();
            const timerFinished = now >= getTargetDate();

            setShowTimer(!timerFinished);

            if (timerFinished && intervalId) {
                clearInterval(intervalId);
            }
        };

        checkTime();
        const interval = window.setInterval(() => checkTime(interval), 1000);

        return () => clearInterval(interval);
    }, []);

    const handleClose = () => {
        const cookies = new Cookies();
        cookies.set(BLACK_FRIDAY_COOKIE_NAME, 'true', {
            path: '/',
            secure: true,
            sameSite: 'lax',
            maxAge: EXPIRATION_60_DAYS_IN_SECONDS,
        });
        setDismissed(true);

        reportClickedCloseIcon(bannerText);
    };

    const handleTermsClick = () => {
        reportClickedTermsConditionsLink(bannerText);
    };

    const getTermsAndConditions = ({ full }: { full: boolean }) => (
        <Link
            href={pathfinder('legal_portal_article_page', {
                category: 'legal-terms',
                page_slug: 'fiverr_black_friday_25',
            })}
            target="_blank"
            color="grey_800"
            decoration="underline"
            size={{ default: 'b_sm', md: 'b_lg' }}
            marginLeft="auto"
            onClick={handleTermsClick}
        >
            {full ? translate('terms_and_conditions') : translate('terms_and_conditions_short')}
        </Link>
    );

    if (dismissed) {
        return null;
    }

    return (
        <Container
            backgroundColor="black"
            paddingLeft={{ default: '4', sm: '5', lg: '8' }}
            paddingRight={{ default: '1', sm: '5', lg: '8' }}
            paddingY={{ default: '3', sm: '5' }}
            width="100%"
            position="relative"
        >
            <Grid className={gridContainer} gap={{ default: '2', sm: '5' }}>
                <GridItem display={{ default: 'none', xl: 'block' }} />

                <GridItem height="100%" direction="row" alignItems="center" gap="5">
                    <Stack
                        height="100%"
                        justifyContent={{ default: 'flexStart', xl: 'center' }}
                        alignItems="center"
                        gap={{ default: '2', sm: '3' }}
                    >
                        {showTimer ? (
                            <Container
                                maxWidth={{ default: '115px', sm: '190px' }}
                                width="100%"
                                className={gradientText}
                                flexShrink={0}
                            >
                                {countdown ? (
                                    <Typography
                                        as="span"
                                        fontSize={{
                                            default: 'b_sm',
                                            sm: 'h_md',
                                        }}
                                        fontWeight="bold"
                                    >
                                        {formatTime(countdown.days)} : {formatTime(countdown.hours)} :{' '}
                                        {formatTime(countdown.minutes)} : {formatTime(countdown.seconds)}
                                    </Typography>
                                ) : (
                                    <Container className={timerSkeleton} />
                                )}
                            </Container>
                        ) : (
                            <Stack direction="column" alignContent="center">
                                <Image
                                    src={DISCOUNT_IMAGE}
                                    alt="Discount"
                                    marginBottom="0.5"
                                    width="81px"
                                    height="26px"
                                    objectFit="contain"
                                />
                            </Stack>
                        )}

                        <Container>
                            <Text
                                color="white"
                                size={{
                                    default: 'b_xs',
                                    sm: 'b_sm',
                                    md: 'b_lg',
                                }}
                            >
                                {bannerText}{' '}
                                <Container as="span" display={{ default: 'inline', sm: 'none' }}>
                                    {getTermsAndConditions({ full: false })}
                                </Container>
                            </Text>
                        </Container>
                    </Stack>
                </GridItem>

                <GridItem display="flex" height="100%">
                    <Stack marginLeft="auto" height="100%" justifyContent="center" alignItems="center">
                        <Container display={{ default: 'none', md: 'block' }}>
                            {getTermsAndConditions({ full: true })}
                        </Container>

                        <Container display={{ default: 'none', sm: 'block', md: 'none' }}>
                            {getTermsAndConditions({ full: false })}
                        </Container>

                        <IconButton
                            aria-label="Close"
                            onClick={handleClose}
                            colorScheme="white"
                            variant="ghost"
                            size="sm"
                        >
                            <TimesIcon />
                        </IconButton>
                    </Stack>
                </GridItem>
            </Grid>
        </Container>
    );
};
